import { useEffect, useMemo, useState } from "react";
import apiClient from "../../../../../helpers/api-client";
import { ProctoringResultViewProps } from "../ProctoringResultView";
import { VerityInstituteSettings } from "@verity/components";
import { OptionRelatedInstituteSettings } from "@verity/proctoring-option/types";
import { canAccessResource } from "../../../../../helpers/privilege";
import { ConfigurationProctoringOptionData } from "../tabs/ConfigurationPanel";
import { getScratchNoteLinks, getSignatureSrc, getStartingSessionIndex } from "./proctoringResultViewHelper";
import { removeAssessmentNameSuffix } from "../../../../../helpers/assessment-name";
import i18n from "../../../../../i18n";
import { useAsync } from "react-use";
import { VerityAssessment } from "@verity/api-client/types";

type useFetchProctoringResultProps = Pick<
	ProctoringResultViewProps,
	| "instituteId"
	| "proctoringSessions"
	| "initialProctoringSessionId"
	| "assessmentInfo"
	| "studentList"
	| "selectedStudent"
	| "courseId"
	| "liveSessions"
	| "exemptedStudents"
>;

export default function useFetchProctoringResult({
	instituteId,
	proctoringSessions,
	initialProctoringSessionId,
	assessmentInfo,
	studentList,
	selectedStudent,
	courseId,
	liveSessions,
	exemptedStudents,
}: useFetchProctoringResultProps) {
	const [optionRelatedInstituteSettings, setOptionRelatedInstituteSettings] =
		useState<OptionRelatedInstituteSettings>();
	const [verityInstituteSettings, setVerityInstituteSettings] = useState<VerityInstituteSettings>();
	const [viewerProctorGroups, setViewerProctorGroups] = useState<{ id: number }[]>([]);
	const [hasInstituteEditPermission, setHasInstituteEditPermission] = useState(false);
	const [instituteSuffix, setInstituteSuffix] = useState<string | undefined>(undefined);
	const [courseName, setCourseName] = useState<string>("Unknown");
	const [verityAssessment, setVerityAssessment] = useState<VerityAssessment | undefined>(undefined);

	const [proctoringConfigData, setProctoringConfigData] = useState<ConfigurationProctoringOptionData>({
		applications: [],
		extensions: [],
		thirdPartyPlatforms: [],
	});

	const [sessionIndex, setSessionIndex] = useState<number>(
		getStartingSessionIndex(proctoringSessions, initialProctoringSessionId),
	);

	const sessionNumber = useMemo(() => {
		return sessionIndex + 1;
	}, [sessionIndex]);

	const selectedProctoringSession = useMemo(() => {
		return proctoringSessions[sessionIndex];
	}, [proctoringSessions, sessionIndex]);

	const sessionUuid = useMemo(() => {
		return selectedProctoringSession?.uuid;
	}, [selectedProctoringSession]);

	const sessionStatus = useMemo(() => {
		return selectedProctoringSession?.status;
	}, [selectedProctoringSession]);

	const userInfo = useMemo(() => selectedProctoringSession?.userInfo, [selectedProctoringSession]);

	const totalSessions = useMemo(() => {
		return proctoringSessions.length;
	}, [proctoringSessions]);

	const assessmentName = useMemo(
		() =>
			removeAssessmentNameSuffix(
				assessmentInfo?.name ??
					(i18n.t("result.components.resultsPage.proctoringResultsViewTSX.unknownAssessment") as string),
				instituteSuffix,
			),
		[assessmentInfo, instituteSuffix],
	);

	const studentName = useMemo(() => {
		return selectedStudent
			? studentList[studentList.findIndex((student) => student.id === selectedStudent)].name
			: (i18n.t("result.components.resultsPage.proctoringResultsViewTSX.unnamedStudent") as string);
	}, [selectedStudent, studentList]);

	const isExemptedNoProctoring = useMemo(() => {
		if (!exemptedStudents || selectedStudent === undefined) return false;
		const student = studentList.find((student) => student.id === selectedStudent);
		if (!student) return false;

		const exemptionInfo = exemptedStudents.find((exemption) => exemption.userId === student.id);

		return exemptionInfo && exemptionInfo.proctoringOptionProfileId === undefined;
	}, [exemptedStudents, studentList, selectedStudent]);

	useEffect(() => {
		void (async () => {
			const course = await apiClient.course.getCourseInfoFromId(courseId);
			if (course.name) {
				setCourseName(course.name);
			}
		})();
	}, [courseId]);

	useEffect(() => {
		async function fetchProctoringData() {
			const userUuid = (await apiClient.getJwt())?.uid;
			const [appRes, extRes, institute, userPrivileges, thirdPartyRes, verityAssessmentRes] = await Promise.all([
				apiClient.verityOptions.getAllApplications().catch(console.error),
				apiClient.verityOptions.getAllExtensions().catch(console.error),
				apiClient.institute.get(instituteId).catch(console.error),
				apiClient.permission.getUserPrivileges().catch(console.error),
				apiClient.institute.getInstituteThirdPartyPlatformsWithInstituteId(instituteId).catch(console.error),
				assessmentInfo?.id
					? apiClient.assessment.getVerityAssessmentInstructor(assessmentInfo.id).catch(console.error)
					: undefined,
				apiClient.institute.getVeritySettings(instituteId).then(setVerityInstituteSettings).catch(console.error),
				userUuid
					? apiClient.liveClient
							.getUserProctorGroups(userUuid, ["proctor", "admin"])
							.then(setViewerProctorGroups)
							.catch(console.error)
					: undefined,
			]);

			if (institute && userPrivileges) {
				const userCanEditInstitute = canAccessResource(
					{ privileges: userPrivileges },
					{
						privilege: "INSTITUTE",
						action: "UPDATE",
						resourceScope: { instituteId: institute.uuid, resourceId: institute.uuid },
					},
				);
				if (userCanEditInstitute) {
					setHasInstituteEditPermission(userCanEditInstitute);
				} else {
					const perm = await apiClient.permission.doesUserHaveInstituteProfilePermissions();
					setHasInstituteEditPermission(perm);
				}
			}

			if (institute?.uuid) {
				try {
					const instituteSettings = await apiClient.institute.getSettings(institute.uuid);
					setOptionRelatedInstituteSettings(instituteSettings);
					setInstituteSuffix(instituteSettings.suffix || "");
				} catch (e) {
					console.error(e);
					setInstituteSuffix("");
				}
			}

			const platforms = thirdPartyRes ? Object.values(thirdPartyRes.selectedPlatformRoster) : [];
			platforms.push({ platformName: "Others", platformId: -1 });

			setProctoringConfigData({
				applications: appRes ?? [],
				extensions: extRes ?? [],
				thirdPartyPlatforms: platforms,
			});

			if (verityAssessmentRes) {
				setVerityAssessment(verityAssessmentRes ?? undefined);
			}
		}

		void fetchProctoringData();
	}, []);

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			const updateSession = () => {
				const selectedSessionIndex = getStartingSessionIndex(proctoringSessions, initialProctoringSessionId);
				setSessionIndex(selectedSessionIndex);
			};
			updateSession();
		}, 300); // 300ms debounce

		return () => clearTimeout(timeoutId);
	}, [proctoringSessions, initialProctoringSessionId]);

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		if (params.has("sessionNum") && params.has("session")) {
			const sessionNum = Number(params.get("sessionNum"));
			const totalSessions = proctoringSessions.length;
			if (sessionNum > 0 && sessionNum <= totalSessions) {
				setSessionIndex(sessionNum - 1);
			}
		}
	}, []);

	const isWebCamDeleted = useMemo(
		() => Boolean(selectedProctoringSession?.isWebcamAndScreenCaptureDeleted),
		[selectedProctoringSession],
	);
	const isRoomScanDeleted = useMemo(
		() => Boolean(selectedProctoringSession?.isRoomScanDeleted),
		[selectedProctoringSession],
	);

	const signatureUrl = useAsync(() => getSignatureSrc(sessionUuid), [sessionUuid]);
	const scratchNoteLinks = useAsync(() => getScratchNoteLinks(sessionUuid), [sessionUuid]);

	const isLiveSessionProctor = useMemo(() => {
		const selectedSessionLiveSession = selectedProctoringSession?.liveSessionId
			? liveSessions[selectedProctoringSession.liveSessionId]
			: undefined;

		const viewerProctorGroupIds = new Set(viewerProctorGroups.map(({ id }) => id));

		return selectedSessionLiveSession?.proctorGroups.some(({ id }) => viewerProctorGroupIds.has(id));
	}, [selectedProctoringSession, liveSessions, viewerProctorGroups]);

	const updateSessionIndex = (newIndex: number) => {
		if (newIndex >= 0 && newIndex < proctoringSessions.length) {
			setSessionIndex(newIndex);
		}
	};

	return {
		state: {
			optionRelatedInstituteSettings,
			verityInstituteSettings,
			hasInstituteEditPermission,
			instituteSuffix,
			proctoringConfigData,
			assessmentName,
			studentName,
			courseName,
			isLiveSessionProctor,
			verityAssessment,
			isExemptedNoProctoring,
		},
		session: {
			sessionUuid,
			sessionNumber,
			totalSessions,
			selectedProctoringSession,
			sessionStatus,
			isWebCamDeleted,
			isRoomScanDeleted,
			signatureUrl,
			scratchNoteLinks,
			userInfo,
			updateSessionIndex,
		},
	};
}
